import { createContext, useContext, useState } from "react";
import { stripHtml } from "../utils/richText";

const QuoteContext = createContext(null);

export function QuoteProvider({ children }) {
  const [items, setItems] = useState([]);

  const getQuoteKey = (product) =>
    product.quoteKey || `${product.id}::${product.selectedVariant?.label || ""}`;

  const addToQuote = (product, variant) => {
    const selectedVariant = variant || product.selectedVariant || product.variants?.[0] || null;
    const availableStock = Number(selectedVariant?.stock ?? product.stock ?? 0);
    if (availableStock <= 0) return;
    const variantPrice = Number(selectedVariant?.price || 0);
    const fallbackPrice = Number(product.priceWithGst ?? product.finalPrice ?? product.price ?? 0);
    const quoteProduct = {
      ...product,
      name: stripHtml(product.name),
      selectedVariant,
      quoteKey: `${product.id}::${selectedVariant?.label || ""}`,
      minQty: Number(selectedVariant?.minQty || product.minQty || 1),
      unit: selectedVariant?.unit || product.unit || "pcs",
      stock: availableStock,
      finalPrice: variantPrice > 0 ? variantPrice : fallbackPrice,
      priceWithGst: variantPrice > 0 ? variantPrice : fallbackPrice,
    };

    setItems((current) => {
      const key = getQuoteKey(quoteProduct);
      const existing = current.find((item) => getQuoteKey(item) === key);
      const step = Number(quoteProduct.minQty || 1);
      if (existing) {
        return current.map((item) => (getQuoteKey(item) === key ? { ...item, quantity: item.quantity + step } : item));
      }
      return [...current, { ...quoteProduct, quantity: step, note: "" }];
    });
  };

  const increase = (key) => setItems((current) => current.map((item) => (getQuoteKey(item) === key ? { ...item, quantity: item.quantity + Number(item.minQty || 1) } : item)));
  const decrease = (id) =>
    setItems((current) =>
      current.map((item) => {
        const step = Number(item.minQty || 1);
        return getQuoteKey(item) === id ? { ...item, quantity: Math.max(step, item.quantity - step) } : item;
      })
    );
  const remove = (id) => setItems((current) => current.filter((item) => getQuoteKey(item) !== id));
  const updateNote = (id, note) => setItems((current) => current.map((item) => (getQuoteKey(item) === id ? { ...item, note } : item)));
  const clearQuote = () => setItems([]);

  const value = {
    items,
    count: items.length,
    totalQuantity: items.reduce((total, item) => total + item.quantity, 0),
    addToQuote,
    increase,
    decrease,
    remove,
    updateNote,
    clearQuote,
  };

  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>;
}

export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (!context) throw new Error("useQuote must be used within QuoteProvider");
  return context;
};
